import { FC, MutableRefObject, useMemo } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { Toast } from 'primereact/toast';
import { allStudentsCount } from '../../graphQLOperations/Students';
import { StudentsCountQuery } from '../../graphQLOperations/__generated__/StudentsCountQuery.graphql';
import { useAppSelector } from '../../redux/hooks';
import { selectUser } from '../../redux/reducers/userSlice';
import { useGetClassesByRole } from '../../custom-hooks/useGetClassesByRole';
import { useHiddenClassIds } from '../../custom-hooks/useHiddenClassIds';
import { getClassesRostersView } from '../../businessLogicService/getClassesRostersView';
import { Class } from '../../models/Class';
import HorizontalLoading from '../../assets/HorizontalLoading/HorizontalLoading';
import ClassesRosterEmptyState from './ClassesRosterEmptyState';
import ClassesRosterGrid from './ClassesRosterGrid';
import ClassesRosterHiddenSection from './ClassesRosterHiddenSection';
import ClassesRosterCreateWizard from './ClassesRosterWizard/ClassesRosterCreateWizard';
import { ClassMenuAction } from './ClassesRosterCardMenu';

interface Props {
  isAdmin: boolean;
  createdClasses: Class[];
  onCreateClass: () => void;
  onMenuAction: (action: ClassMenuAction, cls: Class) => void;
  onUnhide: (cls: Class) => void;
  highlightFirstClass?: boolean;
  wizardVisible: boolean;
  onHideWizard: () => void;
  onClassCreated: (created: Class) => void;
  toast: MutableRefObject<Toast | null>;
}

/** Separate from the body so it can be remounted (via `key`) to force a fresh class-list fetch. */
const ClassesRostersContent: FC<Props> = ({
  isAdmin,
  createdClasses,
  onCreateClass,
  onMenuAction,
  onUnhide,
  highlightFirstClass = false,
  wizardVisible,
  onHideWizard,
  onClassCreated,
  toast,
}) => {
  const userRedux = useAppSelector(selectUser);

  const siteIds = (userRedux?.sites?.map(site => site.id).filter(Boolean) as string[]) ?? [];
  const { allStudents } = useLazyLoadQuery<StudentsCountQuery>(
    allStudentsCount,
    { filter: { siteIds: siteIds.length ? siteIds : null } },
    { fetchPolicy: 'network-only' },
  );
  const studentCount = allStudents?.totalCount ?? 0;

  const hiddenClassIds = useHiddenClassIds();

  // Include hidden classes here so we can split them into their own section.
  const { allClassesLoaded, allClassesData } = useGetClassesByRole(false, false, null, true);

  const mergedClasses = useMemo(() => {
    const byId = new Map<string, Class>();
    (allClassesData ?? []).forEach(cls => cls && byId.set(cls.id, cls));
    createdClasses.forEach(cls => byId.set(cls.id, cls));
    return Array.from(byId.values());
  }, [allClassesData, createdClasses]);

  const { visibleClasses, hiddenClasses } = useMemo(() => {
    const visible: Class[] = [];
    const hidden: Class[] = [];
    mergedClasses.forEach(cls => (hiddenClassIds.has(cls.id) ? hidden : visible).push(cls));
    return { visibleClasses: visible, hiddenClasses: hidden };
  }, [mergedClasses, hiddenClassIds]);

  if (!allClassesLoaded) {
    return <HorizontalLoading />;
  }

  // The empty state considers all classes (hidden ones still count as "has classes").
  const view = getClassesRostersView(mergedClasses.length, studentCount);

  return (
    <>
      {view !== 'classes' ? (
        <ClassesRosterEmptyState variant={view} isAdmin={isAdmin} onCreateClass={onCreateClass} />
      ) : (
        <>
          <ClassesRosterGrid
            classes={visibleClasses}
            isAdmin={isAdmin}
            onCreateClass={onCreateClass}
            highlightFirstClass={highlightFirstClass}
            onMenuAction={(action, classId) => {
              const target = mergedClasses.find(cls => cls.id === classId);
              if (target) onMenuAction(action, target);
            }}
          />
          <ClassesRosterHiddenSection hiddenClasses={hiddenClasses} onUnhide={onUnhide} />
        </>
      )}
      <ClassesRosterCreateWizard
        visible={wizardVisible}
        onHide={onHideWizard}
        onCreated={onClassCreated}
        toast={toast}
        existingClasses={mergedClasses}
      />
    </>
  );
};

export default ClassesRostersContent;
